<template>
    <page :busy="busy" :tabs="tabs" class="account-tsa-page">
        <div v-if="userId" class="pt-3">
            <h6 class="pb-3"><b> Two Step Authentication status: </b>{{ tsaMethod }}</h6>
            <b-button @click="showModal" variant="primary"> Configure Two Step Authentification </b-button>
        </div>
        <modal
            id="config-tsa-modal"
            @ok.prevent="finishTSA"
            title="Setup Two Step Authentication"
            @hidden="clearData"
            scrollable
        >
            <div v-if="mobileAppAuthSteps === 0">
                <p>Authentication Method:</p>
                <b-button-group class="tsa-methods-wrapper">
                    <b-button
                        :active="isNoneTab"
                        exact-active-class="active-nav-button"
                        variant="default"
                        @click="tabIndex = 0"
                        href="#"
                        >None</b-button
                    >
                    <b-button v-if="!userIsTrial" :active="isSMSTab" variant="default" @click="tabIndex = 1" href="#"
                        >SMS</b-button
                    >
                    <b-button :active="isAppTab" variant="default" @click="tabIndex = 2" href="#">Mobile App</b-button>
                    <b-button :active="isKeyTab" variant="default" @click="tabIndex = 3" href="#">Security Key</b-button>
                </b-button-group>
                <div class="pt-3">
                    <p v-if="isNoneTab">Two step authentication will be inactive.</p>
                    <div v-if="isSMSTab">
                        <p>Two step authentication using SMS will be activated.</p>
                        <p class="pb-3">
                            <b>Mobile Phone verified:</b>
                            <span
                                v-if="authProfile.mobile_verified"
                                @click="isCollapseVisible = !isCollapseVisible"
                                class="text-primary masked-number"
                                >{{ maskedNumber }}</span
                            >
                            <font-awesome-icon
                                :icon="['fas', `${authProfile.mobile_verified ? 'check' : 'times'}`]"
                                :color="authProfile.mobile_verified ? 'green' : 'red'"
                            ></font-awesome-icon>
                        </p>
                        <b-collapse v-model="isCollapseVisible" class="mt-2">
                            <div class="d-none d-lg-flex align-items-start">
                                <form-phone-number-input
                                    inputId="sms-phone"
                                    class="w-50"
                                    v-model="authProfile.mobile_phone"
                                    :default-country-code="defaultSmsCountryCode"
                                    @validate="onValidatePhone"
                                    @input="onChangeSmsPhone"
                                ></form-phone-number-input>
                                <b-button
                                    class="ml-2 w-25"
                                    variant="primary"
                                    @click="sendCode"
                                    :disabled="!isPhoneValid"
                                    >Send Code</b-button
                                >
                            </div>
                            <div class="d-flex d-lg-none flex-column align-items-start w-100">
                                <form-phone-number-input
                                    inputId="sms-phone-1"
                                    class="w-100"
                                    v-model="authProfile.mobile_phone"
                                    @validate="onValidatePhone"
                                    @input="onChangeSmsPhone"
                                    :default-country-code="defaultSmsCountryCode"
                                ></form-phone-number-input>
                                <b-button
                                    class="w-100 mb-3"
                                    variant="primary"
                                    :disabled="!isPhoneValid"
                                    @click="sendCode"
                                    >Send Code</b-button
                                >
                            </div>
                            <div class="d-none d-lg-flex align-items-start">
                                <form-input v-model="authProfile.sms_verify_code" class="w-50"></form-input>
                                <b-button class="ml-2 w-25" variant="primary" @click="validateCode"
                                    >Validate Code</b-button
                                >
                            </div>
                            <div class="d-flex d-lg-none w-100 flex-column align-items-start">
                                <form-input v-model="authProfile.sms_verify_code" class="w-100"></form-input>
                                <b-button class="w-100 mb-4" variant="primary" @click="validateCode"
                                    >Validate Code</b-button
                                >
                            </div>
                        </b-collapse>
                    </div>                    
                    <div v-if="isAppTab">
                        <div v-if="mobileAppAuthSteps === 0">
                            <p>
                                We'll ask for codes when you (or anyone else) tries to sign in to your account from
                                other computers or devices. With this option, you'll receive verification codes from the
                                Google Authenticator app. Select your phone type:
                            </p>
                            <b-button-group class="tsa-methods-wrapper">
                                <b-button
                                    :active="phoneTabIndex === 0"
                                    variant="default"
                                    @click="phoneTabIndex = 0"
                                    href="#"
                                    >iPhone</b-button
                                >
                                <b-button
                                    :active="phoneTabIndex === 1"
                                    variant="default"
                                    @click="phoneTabIndex = 1"
                                    href="#"
                                    >Android</b-button
                                >
                            </b-button-group>
                            <div class="pt-3" v-if="phoneTabIndex === 0">
                                <p>Install the Google Authenticator app for iPhone.</p>
                                <ol>
                                    <li>On your iPhone, tap the App Store icon.</li>
                                    <li>
                                        Search for Google Authenticator.
                                        <br />
                                        (<a
                                            target="_blank"
                                            href="http://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8"
                                            >Download from the App Store</a
                                        >)
                                    </li>
                                    <li>Tap the app, and then tap Free to download and install it.</li>
                                    <li>Click Next to proceed to next stage.</li>
                                </ol>
                            </div>
                            <div class="pt-3" v-if="phoneTabIndex === 1">
                                <p>Install the Google Authenticator app for Android.</p>
                                <ol>
                                    <li>On your phone, go to the Google Play Store.</li>
                                    <li>
                                        Search for Google Authenticator.
                                        <br />
                                        (<a
                                            target="_blank"
                                            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                                            >Download from the Google Play Store</a
                                        >)
                                    </li>
                                    <li>Tap the app, and then tap Free to download and install it.</li>
                                    <li>Click Next to proceed to next stage.</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                    <div v-if="isKeyTab">
                        <div class="flex" v-if="keysList.length < 4">
                            <form-input
                                class="enter-code-input"
                                floatingLabel="Security Key Name"
                                v-model="keyName"
                                @handle-right-button-click="registerKey"
                                :rightButton="{
                                    title: '',
                                    text: 'Add',
                                    variant: 'primary',
                                    toggle: false,
                                    disabled: !securityKeyFormMeta.valid,
                                }"
                            ></form-input>
                        </div>
                        <grid
                            ref="grid"
                            :columnDefs="columnDefs"
                            :rowData="keysList"
                            :isSearch="false"
                            :isAutoHeight="true"
                            v-if="keysList.length > 0"
                        ></grid>                        
                    </div>                    
                </div>
            </div>
            <div v-if="mobileAppAuthSteps === 1">
                <p>
                    <b>Now open and configure Google Authenticator.</b>
                </p>
                <p>
                    In Google Authenticator, tap "+", and then "Scan Barcode." Use your phone's camera to scan this
                    barcode.
                </p>
                <div class="d-flex justify-content-center align-items-center">
                    <img alt="qrcode" class="qr-code" :src="qr_code" />
                </div>
                <p>Once you have scanned the barcode, click Next.</p>
            </div>

            <div v-if="mobileAppAuthSteps === 2">
                <p>Now enter the 6-digit verification code generated by the Authenticator app.</p>
                <div class="flex">
                    <form-input
                        class="enter-code-input"
                        floatingLabel="Enter code from mobile app"
                        v-model="codeFromApp"
                        :error="codeFormErrors['codeFromApp']"
                        @handle-right-button-click="verifyCode()"
                        :rightButton="{
                            title: '',
                            text: 'Verify',
                            variant: 'outline-primary',
                            toggle: false,
                            disabled: !codeFormMeta.valid,
                        }"
                    ></form-input>
                </div>
            </div>
            <b-alert v-if="mobileAppAuthSteps === 2" :show="showAlert" variant="success">
                Code was sucessfully verified.<br />
                You can now press Finish to activate Two Step Authentication.
            </b-alert>
            <template #modal-footer="{ ok, cancel }">
                <div>
                    <b-button class="mr-2" variant="secondary" @click="cancel()">Cancel</b-button>
                    <b-button
                        class="mr-2"
                        v-if="isAppTab && mobileAppAuthSteps > 0 && mobileAppAuthSteps <= 2"
                        variant="default"
                        @click="mobileAppAuthSteps--"
                        >Back</b-button
                    >
                    <b-button
                        v-if="((isSMSTab || isNoneTab || (isKeyTab && keysList.length > 0)) && mobileAppAuthSteps === 0) || mobileAppAuthSteps == 2"
                        variant="primary"
                        @click="ok()"
                        >Finish</b-button
                    >
                    <b-button
                        variant="primary"
                        @click="mobileAppAuthSteps++"
                        v-if="isAppTab && mobileAppAuthSteps >= 0 && mobileAppAuthSteps < 2"
                        >Next</b-button
                    >
                </div>
            </template>
        </modal>        
    </page>
</template>

<script>
import { defineComponent, defineAsyncComponent, ref, computed, watch, onMounted } from 'vue';
import { mapGetters, useStore } from 'vuex';
import { useRoute } from 'vue-router';
import API from '../../api';
import {
    ACCOUNT_DETAILS_DATA_ENDPOINT,
    ACCOUNT_TWO_STEP_AUTH_SMS_CODE_ENPOINT,
    ACCOUNT_TWO_STEP_AUTH_VERIFY_SMS_CODE_ENPOINT,
    ACCOUNT_TWO_STEP_AUTH_SET_CONFIG_ENPOINT,
    ACCOUNT_TWO_STEP_AUTH_VERIFY_APP_CODE_ENDPOINT,
    ACCOUNT_WEBAUTHN_START_REGISTRATION,
    ACCOUNT_WEBAUTHN_FINISH_REGISTRATION,
    ACCOUNT_WEBAUTHN_START_AUTHENTICATION,
    ACCOUNT_WEBAUTHN_FINISH_AUTHENTICATION,
    ACCOUNT_WEBAUTHN_LIST_KEYS,
    ACCOUNT_WEBAUTHN_DELETE_KEY,
} from '../../endpoints';
import FormInput from '../generic/form-input/form-input.vue';
import { formatResponseError } from '../../utils/api';
import page from '../generic/page/page.vue';
import { parsePhoneNumber } from 'awesome-phonenumber';
import modal from '../generic/modal/modal.vue';
import { useForm } from 'vee-validate';
import { useToast } from '../../hooks/useToast';
import { useModal } from '../../hooks/useModal';
import Grid from '../generic/grid/grid.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import {
    create as createFido2Key,
    get as getFido2Key,
    parseCreationOptionsFromJSON,
    parseRequestOptionsFromJSON,
} from './webauthn-json.browser-ponyfill';

const FormPhoneNumberInput = defineAsyncComponent(() =>
    import(/* webpackChunkName: "lx-form-phone-number-input" */
    '../generic/form-phone-number-input/form-phone-number-input.vue')
);

export default defineComponent({
    components: { page, FormPhoneNumberInput, FormInput, modal, Grid, actionsCellRenderer: ActionsCellRenderer },

    setup() {
        const store = useStore();
        const userIsTrial = computed(() => store.getters['header/userIsTrial']);
        const authProfile = ref({
            mobile_masked: null,
            mobile_phone: '',
            mobile_verified: null,
            sms_verify_code: null,
            tsa_active: null,
            tsa_method: null,
        });
        const verifiedMobilePhone = ref('');
        const busy = ref(null);
        const qr_code = ref(null);
        const { $bvModal } = useModal();
        const { successToast, errorToast } = useToast();
        const { defineField: codeFormDefineField, meta: codeFormMeta, errors: codeFormErrors } = useForm({
            validationSchema: {
                codeFromApp: 'required',
            },
        });
        const [codeFromApp] = codeFormDefineField('codeFromApp');
        const {
            defineField: securityKeyFormDefineField,
            meta: securityKeyFormMeta,
            errors: securityKeyFormErrors,
        } = useForm({
            validationSchema: {
                keyName: 'required',
            },
        });
        const [keyName] = securityKeyFormDefineField('keyName');
        const isCollapseVisible = ref(null);
        const isAppCodeVerified = ref(null);
        const mobileAppAuthSteps = ref(0);
        const tabs = ref([
            {
                active: false,
                title: 'Account',
                route: '/app/account/details/',
            },
            {
                active: true,
                title: 'Two Step Authentication',
                route: '/app/account/two-step/',
            },
        ]);
        const tabIndex = ref(null);
        const showAlert = ref(null);
        const phoneTabIndex = ref(0);
        const userId = ref(null);
        const isPhoneValid = ref(null);
        const keysList = ref([]);

        const tsaMethod = computed(() => {
            switch (authProfile.value.tsa_method) {
                case 'APP':
                    return 'Google Authenticator';
                case 'SMS':
                    return 'SMS';
                case 'KEY':
                    return 'Security Key';
                default:
                    return 'Disabled';
            }
        });

        const columnDefs = computed(() => {
            return [
                {
                    field: 'created',
                    headerName: 'Created',
                    minWidth: 120,
                    maxWidth: 120,
                    type: 'dateColumn',
                    sortable: false,
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    resizable: false,
                    sortable: false,
                },
                {
                    headerName: '',
                    minWidth: 70,
                    maxWidth: 70,
                    autoHeight: true,
                    resizable: false,
                    sortable: false,
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: ({ data, node }) => {
                        return {
                            onDeleteSecurityKey: () => {
                                onDeleteSecurityKey(data.id);
                            },
                        };
                    },
                },
            ];
        });

        const maskedNumber = computed(() => authProfile.value.mobile_masked);
        const isNoneTab = computed(() => tabIndex.value === 0);
        const isSMSTab = computed(() => tabIndex.value === 1);
        const isAppTab = computed(() => tabIndex.value === 2);
        const isKeyTab = computed(() => tabIndex.value === 3);

        const currentPhoneType = computed(() => (phoneTabIndex.value === 0 ? 'IPHONE' : 'ANDROID'));

        const defaultSmsCountryCode = computed(() => {
            if (authProfile.value.mobile_phone) {
                const phone = parsePhoneNumber(authProfile.value.mobile_phone);
                return phone.g.regionCode;
            } else {
                return store.getters['account/code'];
            }
        });

        const fetchAccountData = async ({ checkInitialTabIndex = true } = {}) => {
            try {
                busy.value = true;
                const response = await API.get(ACCOUNT_DETAILS_DATA_ENDPOINT());
                authProfile.value = {
                    ...response.data.auth_profile,
                };
                qr_code.value = response.data.qr_code;
                userId.value = response.data.client.user.id;
                if (!store.getters['account/code']) {
                    store.dispatch('account/changeCountryCode', response.data.client.address.country?.code || 'US');
                }
                isCollapseVisible.value = !authProfile.value.mobile_verified;
                verifiedMobilePhone.value = authProfile.value.mobile_verified ? authProfile.value.mobile_phone : '';
                if (checkInitialTabIndex) {
                    tabIndex.value = checkInitalTabIndex(authProfile.value.tsa_method);
                }
            } catch (e) {
                errorToast.value({ text: 'Could not load account data' });
            } finally {
                busy.value = false;
            }
        };

        const fetchSecurityKeys = async () => {
            try {
                busy.value = true;
                const response = await API.get(ACCOUNT_WEBAUTHN_LIST_KEYS());
                keysList.value = response.data;
            } catch (e) {
                console.log(e);
                errorToast.value({ text: 'Could not load security keys' });
            } finally {
                busy.value = false;
            }
        };

        const registerKey = async () => {
            try {
                busy.value = true;
                let response = await API.post(ACCOUNT_WEBAUTHN_START_REGISTRATION(), { name: keyName.value });
                let options = parseCreationOptionsFromJSON(response.data);
                response = await createFido2Key(options);
                response = await API.post(ACCOUNT_WEBAUTHN_FINISH_REGISTRATION(), response.toJSON());
                fetchSecurityKeys();
                successToast.value({ text: 'Security Key sucessfuly registered.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                errorToast.value({ text: generic ? generic[0] : e.message });
            } finally {
                keyName.value = '';
                busy.value = false;
            }
        };

        //const authenticateKey = async () => {
        //    try {
        //        busy.value = true;
        //        let response = await API.get(ACCOUNT_WEBAUTHN_START_AUTHENTICATION());
        //        let options = parseRequestOptionsFromJSON(response.data);
        //        response = await getFido2Key(options);
        //        response = await API.post(ACCOUNT_WEBAUTHN_FINISH_AUTHENTICATION(), response.toJSON());
        //        successToast.value({ text: 'Security Key sucessfuly authenticated.' });
        //    } catch (e) {
        //        const { generic } = formatResponseError(e);
        //        errorToast.value({ text: generic ? generic[0] : e.message });
        //    } finally {
        //        busy.value = false;
        //    }
        //};

        const onDeleteSecurityKey = async (id) => {
            try {
                busy.value = true;
                let response = await API.delete(ACCOUNT_WEBAUTHN_DELETE_KEY(id));
                tabIndex.value = 0;
                fetchSecurityKeys();
                successToast.value({ text: 'Security Key sucessfuly deleted.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                errorToast.value({ text: generic ? generic[0] : e.message });
            } finally {
                busy.value = false;
            }
        };

        const checkInitalTabIndex = (status) => {
            switch (status) {
                case 'SMS':
                    return 1;
                case 'NONE':
                    return 0;
                case 'APP':
                    return 2;
                case 'KEY':
                    return 3;
            }
        };

        const sendCode = async () => {
            try {
                await API.post(ACCOUNT_TWO_STEP_AUTH_SMS_CODE_ENPOINT(), {
                    ...authProfile.value,
                });
                successToast.value({ text: 'Code successfully sent to your phone' });
            } catch (e) {
                errorToast.value({ text: 'Could not send sms code' });
            }
        };

        const validateCode = async () => {
            try {
                await API.post(ACCOUNT_TWO_STEP_AUTH_VERIFY_SMS_CODE_ENPOINT(), {
                    ...authProfile.value,
                });
                authProfile.value.sms_verify_code = '';
                isCollapseVisible.value = false;
                await fetchAccountData({ checkInitialTabIndex: false });
                successToast.value({ text: 'Mobile Phone successfully verified.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                errorToast.value({ text: generic[0] });
            }
        };

        const verifyCode = async () => {
            try {
                await API.post(ACCOUNT_TWO_STEP_AUTH_VERIFY_APP_CODE_ENDPOINT(), {
                    code: codeFromApp.value,
                });
                isAppCodeVerified.value = true;
                showAlert.value = true;
            } catch (e) {
                errorToast.value({ text: 'Entered code is invalid.' });
            }
        };

        const onValidatePhone = (value) => {
            if (value.isValid) {
                authProfile.value.mobile_phone = value.number.e164;
                isPhoneValid.value = true;
            }
        };

        const onChangeSmsPhone = (value, isValid) => {
            isPhoneValid.value = isValid;
        };

        const finishTSA = async () => {
            if (
                (isAppTab.value && !isAppCodeVerified.value) ||
                (isSMSTab.value && !authProfile.value.mobile_verified)
            ) {
                errorToast.value({ text: 'Authentication method not yet verified.' });
                return;
            } else if (isKeyTab.value && keysList.value.length === 0) {
                errorToast.value({ text: 'At least one security key is required.' });
                return;
            }
            try {
                await API.put(ACCOUNT_TWO_STEP_AUTH_SET_CONFIG_ENPOINT(), {
                    method: isNoneTab.value ? 'NONE' : isSMSTab.value ? 'SMS' : isKeyTab.value ? 'KEY' : 'APP',
                });
                $bvModal.value.hide('config-tsa-modal');
                await fetchAccountData();
                successToast.value({ text: 'Two Step Authentication successfully configured.' });
            } catch (e) {
                errorToast.value({ text: 'Could not finish Two Step Authentication' });
            }
        };

        const showModal = () => {
            $bvModal.value.show('config-tsa-modal');
        };

        const clearData = () => {
            isAppCodeVerified.value = null;
            showAlert.value = null;
            mobileAppAuthSteps.value = 0;
            codeFromApp.value = null;
            isCollapseVisible.value = !authProfile.value.mobile_verified;
            authProfile.value.mobile_phone = verifiedMobilePhone.value;
        };

        onMounted(() => {
            fetchAccountData();
        });

        watch(tabIndex, (newValue, oldValue) => {
            if (newValue === 3) {
                fetchSecurityKeys();
            }
        });

        return {
            authProfile,
            verifiedMobilePhone,
            busy,
            qr_code,
            codeFromApp,
            isCollapseVisible,
            isAppCodeVerified,
            mobileAppAuthSteps,
            tabs,
            tabIndex,
            showAlert,
            phoneTabIndex,
            userId,
            isPhoneValid,
            tsaMethod,
            maskedNumber,
            isNoneTab,
            isSMSTab,
            isAppTab,
            isKeyTab,
            currentPhoneType,
            defaultSmsCountryCode,
            fetchAccountData,
            checkInitalTabIndex,
            sendCode,
            validateCode,
            verifyCode,
            onValidatePhone,
            onChangeSmsPhone,
            finishTSA,
            showModal,
            clearData,
            codeFormMeta,
            codeFormErrors,
            userIsTrial,
            status,
            registerKey,
            //authenticateKey,
            keyName,
            securityKeyFormMeta,
            securityKeyFormErrors,
            fetchSecurityKeys,
            keysList,
            columnDefs,
        };
    },
});
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.account-tsa-page {
    height: max-content !important;
    padding: 0 15px;
    ::deep(ul.nav) {
        margin-left: 0 !important;
        margin-bottom: 0 !important;
        .router-link-active {
            background-color: #f7f7f7 !important;
            margin-bottom: 0 !important;
            border-bottom-color: transparent;
        }
    }
    ::deep(.lx-page-container) {
        padding: 0 15px 30px 15px;
        background-color: #f7f7f7;
        border-bottom: 1px solid #ddd;
        border-left: 1px solid #ddd;
        border-radius: 0 5px 5px 5px;
        border-right: 1px solid #ddd;
    }
}

.masked-number {
    cursor: pointer;
}

.tsa-methods-wrapper {
    a.active {
        background-color: transparent !important;
        border-color: #ccc !important;
        box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.3) !important;
    }
}
.qr-code {
    height: 200px;
    width: 200px;
}
.verify-code-button {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    height: 39px;
}
.enter-code-input {
    input {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        border-right: none;
    }
}

.security-keys-table {
    width: 100%;
    border-collapse: collapse;
}

.security-keys-table th,
.security-keys-table td {
    padding: 10px;
    text-align: left;
}

.security-keys-table tr:hover {
    background-color: #f5f5f5;
}

@include media-breakpoint-down(xs) {
    .verified-phone-wrapper {
        width: 100% !important;
    }
    ::deep(.verify-code-button) {
        height: 44px;
    }
}
</style>
